import React, { useEffect, useId, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import Button from '../../components/ui/Button';

/**
 * A confirmation that carries a note: "Vendor refused", "Send back", "Reverse"
 * on a vendor return (D44). The note lands in the return's history (and, for a
 * refusal or a reversal, on the return itself), so it is a real field rather than
 * window.prompt(). useConfirm has no field, which is why this is not a confirm.
 *
 * Rendered only while open; the page mounts it when an action asks.
 */
export const NoteDialog: React.FC<{
  title: string;
  summary?: string;
  /** What the action does that must not be missed, shown above the note. */
  warning?: string;
  noteLabel: string;
  /** The action cannot be confirmed with a blank note (the server refuses it too). */
  noteRequired?: boolean;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (note: string) => void;
}> = ({
  title,
  summary,
  warning,
  noteLabel,
  noteRequired = false,
  confirmLabel,
  danger = false,
  busy = false,
  onCancel,
  onConfirm,
}) => {
  const [note, setNote] = useState('');
  const missingNote = noteRequired && note.trim() === '';
  const fieldId = useId();
  const fieldRef = useRef<HTMLTextAreaElement>(null);

  // Modal focuses its own panel a tick after opening; the note is what is typed
  // next, so the field takes the focus back once that has happened.
  useEffect(() => {
    const t = window.setTimeout(() => fieldRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <Modal isOpen title={title} onClose={onCancel} maxWidthClass="max-w-md">
      <div className="space-y-3">
        {summary && <p className="text-xs text-slate-600">{summary}</p>}
        {warning && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">{warning}</div>
        )}
        <div>
          <label htmlFor={fieldId} className="block text-xs font-medium text-slate-600 mb-1">
            {noteLabel}
          </label>
          <textarea
            id={fieldId}
            ref={fieldRef}
            rows={3}
            maxLength={1000}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs focus:border-primary-500 resize-y"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onCancel} disabled={busy}>
            Back
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            onClick={() => onConfirm(note.trim())}
            loading={busy}
            disabled={missingNote}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default NoteDialog;
